// Service Worker cleanup.
// We no longer use a caching service worker (it served stale builds after
// deploys). This unregisters any existing service worker and clears all caches
// so the app always loads the latest build fresh from the network. The
// self-destroying /service-worker.js handles devices that still have an old SW.
export const registerServiceWorker = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        // An old caching SW exists on this device. Load the self-destroying SW
        // once so it clears caches, unregisters, and reloads to the latest build.
        try {
          await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
        } catch (e) {
          // fall back to manual unregister below
          await Promise.all(registrations.map((reg) => reg.unregister()));
        }
      }
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (e) {
    // no-op
  }
  return null;
};

// Unregister Service Worker
export const unregisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.unregister();
    console.log('Service Worker unregistered');
  }
};

// Clear all caches and refresh
export const clearAllCaches = async () => {
  try {
    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('All caches cleared');
    
    // Unregister service worker
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      console.log('Service workers unregistered');
    }
    
    // Clear localStorage (except auth)
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    localStorage.clear();
    if (token) localStorage.setItem('token', token);
    if (user) localStorage.setItem('user', user);
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    return true;
  } catch (error) {
    console.error('Failed to clear caches:', error);
    return false;
  }
};

// Force refresh the page with cache bypass
export const forceRefresh = () => {
  window.location.reload(true);
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

// Subscribe to push notifications
export const subscribeToPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Subscribe to push
      // Note: You would need a VAPID key from your server
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }
    
    console.log('Push subscription:', subscription);
    return subscription;
  } catch (error) {
    console.error('Push subscription failed:', error);
    return null;
  }
};

// Show local notification
export const showNotification = async (title, options = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    console.log('Notifications not available');
    return;
  }
  
  const registration = await navigator.serviceWorker.ready;
  
  await registration.showNotification(title, {
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-72x72.png',
    vibrate: [100, 50, 100],
    ...options
  });
};

// Check if app is installed
export const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');
};

// Check online status
export const isOnline = () => navigator.onLine;

// Add online/offline listeners
export const addNetworkListeners = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};
